import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { getBackendBaseUrl } from "@/lib/backendUrl";

const BACKEND_BASE = getBackendBaseUrl();

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Resolve media URLs coming from the API (handles relative paths like /api/uploads/...)
export function resolveMediaUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  if (url.startsWith('/')) {
    if (BACKEND_BASE) return `${BACKEND_BASE}${url}`;
    if (typeof window !== 'undefined') return `${window.location.origin}${url}`;
  }
  return url;
}

// Format date to locale string
export function formatDate(dateString, options = {}) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...options
  });
}

// Format time
export function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Format date and time together
export function formatDateTime(dateString) {
  if (!dateString) return '';
  return `${formatDate(dateString)} ${formatTime(dateString)}`;
}

// Get relative time (e.g., "2 hours ago", "in 3 days")
export function getRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Oggi';
  if (diffDays === 1) return 'Domani';
  if (diffDays === -1) return 'Ieri';
  if (diffDays > 0 && diffDays <= 7) return `Tra ${diffDays} giorni`;
  if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} giorni fa`;
  
  return formatDate(dateString);
}

// Check if date is in the past
export function isPast(dateString) {
  if (!dateString) return false;
  return new Date(dateString) < new Date();
}

// Check if date is today
export function isToday(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

// Get sport icon name
export function getSportIcon(sportId) {
  const icons = {
    'sport_calcetto': 'futbol',
    'sport_padel': 'table-tennis-paddle-ball',
    'sport_tennis': 'baseball',
    'sport_beach_volley': 'volleyball'
  };
  return icons[sportId] || 'circle';
}

// Get sport color class
export function getSportColorClass(sportId) {
  const classes = {
    'sport_calcetto': 'sport-calcetto',
    'sport_padel': 'sport-padel',
    'sport_tennis': 'sport-tennis',
    'sport_beach_volley': 'sport-beach-volley'
  };
  return classes[sportId] || 'bg-muted text-muted-foreground';
}

// Get status color class
export function getStatusColorClass(status) {
  const classes = {
    'draft': 'status-draft',
    'confirmed': 'status-confirmed',
    'cancelled': 'status-cancelled',
    'completed': 'status-completed',
    'pending': 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
    'approved': 'status-confirmed',
    'rejected': 'status-cancelled',
    'requested': 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
  };
  return classes[status] || 'bg-muted text-muted-foreground';
}

// Get status label in Italian
export function getStatusLabel(status) {
  const labels = {
    'draft': 'Bozza',
    'confirmed': 'Confermata',
    'cancelled': 'Annullata',
    'completed': 'Completata',
    'pending': 'In attesa',
    'approved': 'Approvato',
    'rejected': 'Rifiutato',
    'requested': 'Richiesto',
    'registration': 'Iscrizioni aperte',
    'in_progress': 'In corso'
  };
  return labels[status] || status;
}

// Get role label in Italian
export function getRoleLabel(role) {
  const labels = {
    'admin': 'Admin',
    'player': 'Giocatore',
    'referee': 'Arbitro',
    'manager': 'Gestore',
    'league_manager': 'Gestore Lega'
  };
  return labels[role] || role;
}

// Get skill level label
export function getSkillLevelLabel(level) {
  const labels = {
    1: 'Principiante',
    2: 'Base',
    3: 'Intermedio',
    4: 'Avanzato',
    5: 'Esperto'
  };
  return labels[level] || `Livello ${level}`;
}

// Truncate text
export function truncate(text, length = 100) {
  if (!text) return '';
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
}

// Generate initials from name
export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Validate email
export function isValidEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

// Days of week in Italian
export const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Lunedì' },
  { value: 'tuesday', label: 'Martedì' },
  { value: 'wednesday', label: 'Mercoledì' },
  { value: 'thursday', label: 'Giovedì' },
  { value: 'friday', label: 'Venerdì' },
  { value: 'saturday', label: 'Sabato' },
  { value: 'sunday', label: 'Domenica' }
];

// Time slots
export const TIME_SLOTS = [
  { value: 'morning', label: 'Mattina (8-12)' },
  { value: 'afternoon', label: 'Pomeriggio (12-18)' },
  { value: 'evening', label: 'Sera (18-23)' }
];

// Announcement types in Italian
export const ANNOUNCEMENT_TYPES = [
  { value: 'looking_for_players', label: 'Cerco giocatori' },
  { value: 'looking_for_match', label: 'Cerco partita' },
  { value: 'general', label: 'Generale' }
];
